import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Lock, Eye, FileText } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Modern Navigation */}
      <nav className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-start items-center">
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/VetriaAI.png" alt="VetriaAI" className="h-8 w-8 sm:h-9 sm:w-9" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary via-red-700 to-primary bg-clip-text text-transparent group-hover:from-red-800 group-hover:via-primary group-hover:to-red-800 transition-all duration-300">
              VetriaAI
            </span>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-8 sm:py-12 md:py-16 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-50 via-white to-blue-50 opacity-50"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(185,28,28,0.1),transparent_50%)]"></div>

        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-primary/10 rounded-full mb-4 sm:mb-6">
            <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Last Updated: October 9, 2025
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-8 sm:py-12 bg-gradient-to-b from-white to-gray-50 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-card shadow-card border border-gray-100 p-6 sm:p-8 md:p-10">
            <div className="prose prose-sm sm:prose-base max-w-none">
              <div className="space-y-6 sm:space-y-8 text-gray-700 leading-relaxed">

                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    Introduction
                  </h2>
                  <p className="text-sm sm:text-base mb-4">
                    This Privacy Policy explains how VetriaAI processes personal data for HR teams, recruiters,
                    candidates, and visitors. We are committed to protecting your privacy and complying with applicable
                    data protection laws, including the Information Technology Act, 2000, and the Information Technology
                    (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">1. Who We Are</h3>
                  <p className="text-sm sm:text-base mb-2">
                    <strong>VETRIAAI INNOVATION PRIVATE LIMITED</strong>
                  </p>
                  <p className="text-sm sm:text-base mb-2">
                    Registered Office: H. No. 5-95/1, Flat No. G, Mokkapati Towers, Fathenagar Colony,
                    K.v.rangareddy, Balanagar, Telangana, India, 500018
                  </p>
                  <p className="text-sm sm:text-base">
                    <strong>Contact:</strong> <a href="mailto:info@vetriaai.com" className="text-primary hover:underline">info@vetriaai.com</a>
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">2. Scope</h3>
                  <p className="text-sm sm:text-base">
                    This policy covers our website, applications, APIs, and services related to intelligent test generation,
                    candidate screening, analytics, and verification badges.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Eye className="w-5 h-5 text-primary" />
                    3. Data We Collect
                  </h3>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                    <li><strong>Account & Workspace Data:</strong> Names, emails, phone (optional), company, role, login metadata, MFA settings</li>
                    <li><strong>Recruitment Data:</strong> Job descriptions, resumes/CVs, assessments, scores, interview notes, badges/certifications</li>
                    <li><strong>Usage & Device Data:</strong> IP addresses, device identifiers, logs, performance metrics, cookie IDs</li>
                    <li><strong>Payment Data:</strong> Billing contact, address, transaction metadata (processed by PCI-compliant providers)</li>
                    <li><strong>Support & Feedback:</strong> Tickets, chats, survey responses</li>
                    <li><strong>Integrations:</strong> Data exchanged with ATS/CRM/communication tools per your configuration</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">4. How We Use Data</h3>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                    <li>Provide, secure, and improve the Service</li>
                    <li>Generate tests and insights; verify skills; issue badges</li>
                    <li>Configure integrations; provide analytics dashboards</li>
                    <li>Detect, prevent, and investigate fraud, abuse, and security incidents</li>
                    <li>Communicate about service updates, billing, and support</li>
                    <li>Comply with legal obligations under Indian laws</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">5. Legal Basis for Processing</h3>
                  <p className="text-sm sm:text-base mb-2">
                    We process your data based on:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                    <li><strong>Contractual necessity:</strong> Providing the Service as per our agreement</li>
                    <li><strong>Legitimate interests:</strong> Security, product improvement, analytics</li>
                    <li><strong>Consent:</strong> Where required—for certain cookies, marketing, or candidate processing</li>
                    <li><strong>Legal obligations:</strong> Tax, compliance with Indian laws</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">6. Automated Processing & Human Review</h3>
                  <p className="text-sm sm:text-base">
                    Intelligent automation assists with screening and assessments. Automated suggestions do not replace human decisions.
                    Users can request human review of significant outcomes affecting them.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">7. Sharing & Disclosure</h3>
                  <p className="text-sm sm:text-base mb-2">We share data with:</p>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                    <li>Processors (cloud hosting, email, analytics, payment) under data processing agreements</li>
                    <li>Integrations you enable (ATS/CRM/communications)</li>
                    <li>Legal authorities when required by law or court orders under Indian jurisdiction</li>
                  </ul>
                  <p className="text-sm sm:text-base mt-2">
                    <strong>We do not sell personal data.</strong>
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">8. International Transfers</h3>
                  <p className="text-sm sm:text-base">
                    Data may be processed in multiple regions with geographic redundancy. We ensure appropriate safeguards
                    are in place for any international data transfers, in compliance with applicable Indian data protection laws.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Lock className="w-5 h-5 text-primary" />
                    9. Security
                  </h3>
                  <p className="text-sm sm:text-base mb-2">
                    We implement reasonable security practices and procedures as required under Indian law, including:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                    <li>Encryption at rest and in transit (AES-256/TLS)</li>
                    <li>Multi-factor authentication (MFA), role-based access control (RBAC)</li>
                    <li>Audit trails, 24/7 monitoring, DDoS protection</li>
                    <li>Daily backups and disaster recovery procedures</li>
                    <li>Regular security audits and assessments</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">10. Data Retention</h3>
                  <p className="text-sm sm:text-base">
                    Data retention follows your workspace settings by default. We minimize data and purge when no longer needed.
                    On account closure, data is securely deleted after required retention periods unless law requires longer storage
                    (e.g., tax records as per Indian tax laws).
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">11. Your Rights</h3>
                  <p className="text-sm sm:text-base mb-2">
                    Under applicable Indian laws, you may:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm sm:text-base ml-2">
                    <li>Access, correct, or delete your data</li>
                    <li>Object to or restrict processing; withdraw consent</li>
                    <li>Request data portability</li>
                    <li>File a complaint with the relevant data protection authority</li>
                  </ul>
                  <p className="text-sm sm:text-base mt-2">
                    <strong>To exercise your rights:</strong> Contact us at <a href="mailto:info@vetriaai.com" className="text-primary hover:underline">info@vetriaai.com</a>.
                    We may verify your identity before processing requests.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">12. Cookies & Tracking</h3>
                  <p className="text-sm sm:text-base">
                    We use essential cookies for login and security, and analytics/functional cookies with consent where required.
                    You can manage preferences via our cookie banner or browser settings.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">13. Children's Data</h3>
                  <p className="text-sm sm:text-base">
                    Our Service is not intended for children under 18 years of age. We don't knowingly collect data from children.
                    If you believe we have collected data from a child, please contact us immediately.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">14. Grievance Officer</h3>
                  <p className="text-sm sm:text-base mb-2">
                    As required under the Information Technology Act, 2000, we have appointed a Grievance Officer:
                  </p>
                  <p className="text-sm sm:text-base mb-2">
                    <strong>Email:</strong> <a href="mailto:info@vetriaai.com" className="text-primary hover:underline">info@vetriaai.com</a>
                  </p>
                  <p className="text-sm sm:text-base">
                    <strong>Address:</strong> H. No. 5-95/1, Flat No. G, Mokkapati Towers, Fathenagar Colony,
                    K.v.rangareddy, Balanagar, Telangana, India, 500018
                  </p>
                  <p className="text-sm sm:text-base mt-2">
                    Grievances will be addressed within 30 days as per applicable Indian laws.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">15. Changes to this Policy</h3>
                  <p className="text-sm sm:text-base">
                    Updates will be posted here with a revised "Last updated" date. Material changes will be communicated
                    through the app or by email. Continued use of the Service after changes constitutes acceptance.
                  </p>
                </div>

                <div className="bg-primary/5 border-l-4 border-primary p-4 sm:p-6 rounded-r-lg">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">Contact Us</h3>
                  <p className="text-sm sm:text-base mb-2">
                    For privacy-related inquiries or to exercise your rights:
                  </p>
                  <p className="text-sm sm:text-base mb-2">
                    <strong>Email:</strong> <a href="mailto:info@vetriaai.com" className="text-primary hover:underline">info@vetriaai.com</a>
                  </p>
                  <p className="text-sm sm:text-base">
                    <strong>Address:</strong> H. No. 5-95/1, Flat No. G, Mokkapati Towers, Fathenagar Colony,
                    K.v.rangareddy, Balanagar, Telangana, India, 500018
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 sm:py-12 px-4 sm:px-6 mt-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center text-gray-400">
            <p className="text-sm sm:text-base">&copy; 2025 VetriaAI. All copyrights are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
